import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert,
  CircularProgress, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, IconButton, Modal, Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import { formatCurrency } from '../../utils/format';
import ExpensesDetail from '../../../Finance/ExpensesDetail';
import OcrVerifyDialog from './OcrVerifyDialog';

// สถานะต่อแถวในตาราง — 4 แบบ:
//  ยืนยันแล้ว          = ocr_verified_company_account (payment ระบบ หรือรายการเงาที่ฝ่ายการเงินยืนยัน)
//  รอฝ่ายการเงินระบุ   = รายการเงา (is_external) ที่ยังไม่ถูกระบุ/ยืนยัน — ทำที่การ์ด "รายการที่ไม่มีใน payment ระบบ"
//  ไม่พบในรูป          = payment ระบบที่ OCR เทียบแล้วไม่เจอตัวมันในรูป (not_found/mismatched) — ทำที่การ์ด "payment ในระบบที่ไม่พบในธนาคาร"
//  ยังไม่ยืนยัน         = payment ระบบที่ยังไม่เคยเอาไป OCR เทียบ — กด "ยืนยันรายการ OCR" ของวันนั้น
function StatusChip({ row }) {
  if (row.ocr_verified_company_account) {
    return <Chip size="small" color="success" label="ยืนยันแล้ว" />;
  }
  if (row.is_external) {
    return <Chip size="small" color="warning" label="รอฝ่ายการเงินระบุ" />;
  }
  if (row.ocr_not_found) {
    return (
      <Tooltip title={'OCR เทียบแล้วไม่พบรายการนี้ในรูป — จัดการต่อที่การ์ด "payment ในระบบที่ไม่พบในธนาคาร" หน้าบัญชีบริษัท'}>
        <Chip size="small" color="error" label="ไม่พบในรูป" />
      </Tooltip>
    );
  }
  return <Chip size="small" color="warning" label="ยังไม่ยืนยัน" />;
}

// รายการรายรับ-รายจ่ายของวันที่เดียว (คลิกจาก UnverifiedExpenseDatesCard) — โชว์เป็นตาราง
// (ไม่ใช่ accordion) ระดับ payment ไม่ใช่ระดับ item — ต่างจาก CompanyLedgerTable ที่ตั้งใจ
// แยกทีละ item เพราะที่นั่นต้องการดู breakdown ละเอียด แต่ที่นี่ผู้ใช้อยากรวม item ในใบ
// เดียวกัน (+กันเป็นยอดเดียว) เพื่อให้ตารางเทียบกับรูปสลิป/หน้าจอธนาคารได้ง่ายกว่า (1 แถว
// ในรูป = 1 payment พอดี ไม่ใช่หลายแถวย่อย) — ดึงข้อมูลดิบจาก getCustomerExpensesByDate
// (ยังเป็น item-level เหมือนเดิม ไม่แก้ backend) แล้ว group เป็น payment-level เองฝั่งนี้
// (groupedRows) — แยกคอลัมน์รายรับ/รายจ่าย (row.direction, เป็น property ระดับ payment
// อยู่แล้วไม่มีทางต่างกันระหว่าง item ในใบเดียวกัน) เหมือน CompanyLedgerTable เพราะตอนนี้
// ต้องยืนยัน OCR ทั้งสองฝั่งแล้ว ไม่ใช่แค่ฝั่งรายจ่ายเหมือนเดิม — ปุ่ม "ยืนยันรายการ OCR"
// เปิด OcrVerifyDialog เดิม แต่ล็อก dateFrom/dateTo ให้เป็นวันนี้วันเดียวเป๊ะ (รูปที่แนบมา
// โชว์รายการของทั้งวันอยู่แล้ว ไม่ต้องให้ match ข้ามไปวันอื่น)
// statusFilter: 'all' (default) | 'verified' | 'unverified' — กรองแถวที่แสดงตามแท็บที่เปิดมา
//   'verified'   = แท็บ "สำเร็จแล้ว" โชว์เฉพาะรายการที่ยืนยันแล้ว (payment ระบบ + รายการเงาที่ฝ่ายการเงินยืนยัน)
//   'unverified' = แท็บ "รอยืนยันอีกครั้ง" โชว์เฉพาะที่ยังค้าง (payment ระบบยัง unverified + รายการเงา pending)
export function DayExpensesModal({
  open, onClose, customerId, date, companyOpeningBalance, companyOpeningBalanceDate, onVerified,
  statusFilter = 'all',
}) {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);
  // คลิกแถว payment → modal ซ้อน โชว์รายการเบิก (expense item) ของ payment ใบนั้น (read-only)
  // ดึงจาก rows ที่โหลดมาแล้ว (item-level) ไม่ต้อง fetch เพิ่ม — ปุ่ม "ดูรายละเอียด" ต่อแถวเปิด
  // <ExpensesDetail> ตัวเดียวกับที่ CompanyLedgerTable ใช้
  const [paymentDetail, setPaymentDetail] = useState(null);
  const [detailExpenseId, setDetailExpenseId] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const openExpenseDetail = (id) => {
    setDetailExpenseId(id);
    setDetailModalOpen(true);
  };

  const load = useCallback(async () => {
    if (!customerId || !date) return;
    setLoading(true);
    setError('');
    try {
      const data = await dispatch(actions.listCustomerExpensesByDate({ customerId, date }));
      setRows(data.rows || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [customerId, date, dispatch]);

  useEffect(() => {
    if (open) {
      load();
    } else {
      setPaymentDetail(null);
      setDetailModalOpen(false);
    }
  }, [open, load]);

  // รายการเบิก (item-level) ของ payment ที่คลิก — external row ไม่มี expense ในระบบ
  const paymentExpenseItems = useMemo(
    () => (paymentDetail
      ? rows.filter((r) => !r.is_external && r.payment_id === paymentDetail.id)
      : []),
    [rows, paymentDetail],
  );

  // สรุประดับ payment (ไม่ใช่ item) ให้ OcrVerifyDialog ใช้โชว์ preview "รายจ่ายที่ยังไม่
  // ยืนยันในระบบ" ก่อนอัปโหลดรูป — totalAmount ที่นี่รวมจาก item amount เอา (ไม่ตรงเป๊ะกับ
  // payment.totalAmount 100% ถ้ามี field อื่นปนอยู่ แต่ใกล้เคียงพอสำหรับพรีวิวก่อนยืนยัน)
  const systemPaymentsForOcr = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (r.is_external) return; // รายการเงาไม่ต้องส่งเข้า preview ของ OcrVerifyDialog
      if (!map.has(r.payment_id)) {
        map.set(r.payment_id, {
          _id: r.payment_id, payment_number: r.payment_number, date: r.date, totalAmount: 0,
        });
      }
      map.get(r.payment_id).totalAmount += Number(r.amount || 0);
    });
    return Array.from(map.values());
  }, [rows]);

  // รวม item ในใบเดียวกัน (+กันเป็นยอดเดียว) ให้ตารางที่แสดงผลจริงเป็นระดับ payment —
  // คอลัมน์ "รายการ" ใช้ payee_name (ชื่อผู้รับเงิน, payment.payee.name) แทนชื่อ item เพราะ
  // เป็น field ระดับ payment อยู่แล้ว (ไม่มีทางต่างกันระหว่าง item ในใบเดียวกัน ต่างจาก
  // ชื่อ item ที่อาจมีหลายชื่อไม่ซ้ำกันถ้า payment มีหลายรายการ ต้อง join คั่นจุลภาคซึ่งไม่
  // ตรงกับที่ผู้ใช้อยากได้)
  const groupedRows = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (!map.has(r.payment_id)) {
        map.set(r.payment_id, {
          id: r.payment_id,
          payment_number: r.payment_number,
          date: r.date,
          direction: r.direction,
          ocr_verified_company_account: r.ocr_verified_company_account,
          is_external: Boolean(r.is_external),
          ocr_not_found: Boolean(r.ocr_not_found),
          name: r.payee_name || '-',
          amount: 0,
        });
      }
      map.get(r.payment_id).amount += Number(r.amount || 0);
    });
    return Array.from(map.values());
  }, [rows]);

  // กรองตามแท็บที่เปิดมา (ดู comment ที่ prop statusFilter)
  const displayRows = useMemo(() => {
    if (statusFilter === 'verified') return groupedRows.filter((r) => r.ocr_verified_company_account);
    if (statusFilter === 'unverified') return groupedRows.filter((r) => !r.ocr_verified_company_account);
    return groupedRows;
  }, [groupedRows, statusFilter]);

  const allVerified = displayRows.length > 0 && displayRows.every((r) => r.ocr_verified_company_account);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          รายการรายรับ-รายจ่ายวันที่ {date ? new Date(date).toLocaleDateString('th-TH') : ''}
          {displayRows.length > 0 && (
            <Chip
              size="small"
              sx={{ ml: 1 }}
              color={allVerified ? 'success' : 'warning'}
              label={allVerified ? 'ยืนยันแล้ว' : 'ยังไม่ยืนยัน'}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>เลขที่เอกสาร</TableCell>
                    <TableCell>ผู้รับเงิน</TableCell>
                    <TableCell align="right">รายรับ (บาท)</TableCell>
                    <TableCell align="right">รายจ่าย (บาท)</TableCell>
                    <TableCell>สถานะ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>ไม่มีรายการ</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {displayRows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover={!row.is_external}
                      onClick={() => { if (!row.is_external) setPaymentDetail(row); }}
                      sx={{ cursor: row.is_external ? 'default' : 'pointer' }}
                    >
                      <TableCell>{row.payment_number}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Tooltip title={row.name}>
                          <Typography variant="body2" noWrap>{row.name}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        {row.direction === 'income' ? formatCurrency(row.amount) : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {row.direction === 'expense' ? formatCurrency(row.amount) : '-'}
                      </TableCell>
                      <TableCell>
                        <StatusChip row={row} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>ปิด</Button>
          {statusFilter !== 'verified' && (
            <Button variant="contained" onClick={() => setOcrDialogOpen(true)} disabled={rows.length === 0}>
              ยืนยันรายการ OCR
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(paymentDetail)} onClose={() => setPaymentDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>รายการเบิกของ {paymentDetail?.payment_number || ''}</span>
          <IconButton size="small" onClick={() => setPaymentDetail(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>รายการ</TableCell>
                  <TableCell>โครงการ</TableCell>
                  <TableCell>ผู้รับเงิน</TableCell>
                  <TableCell align="right">ยอด (บาท)</TableCell>
                  <TableCell align="center">รายละเอียด</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentExpenseItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>ไม่มีรายการเบิกในระบบ</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {paymentExpenseItems.map((it) => (
                  <TableRow key={it.id} hover>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Tooltip title={it.name || '-'}>
                        <Typography variant="body2" noWrap>{it.name || '-'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 160 }}>
                      <Tooltip title={it.project_name || '-'}>
                        <Typography variant="body2" noWrap>{it.project_name || '-'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 160 }}>
                      <Tooltip title={it.payee_name || '-'}>
                        <Typography variant="body2" noWrap>{it.payee_name || '-'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(it.amount)}</TableCell>
                    <TableCell align="center">
                      {it.expenses_id ? (
                        <Tooltip title="ดูรายละเอียดรายการเบิกจ่าย">
                          <IconButton size="small" onClick={() => openExpenseDetail(it.expenses_id)}>
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDetail(null)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      <Modal open={detailModalOpen} onClose={() => setDetailModalOpen(false)} closeAfterTransition>
        <Fade in={detailModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '98%', sm: '95%', md: '90%', lg: '82%' },
              maxHeight: '95vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'primary.main',
                color: 'white',
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">รายละเอียดรายการเบิกจ่าย</Typography>
              <IconButton size="small" onClick={() => setDetailModalOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ overflowY: 'auto', flex: 1, p: { xs: 1, sm: 2 } }}>
              {detailExpenseId && (
                <ExpensesDetail propId={detailExpenseId} onClose={() => setDetailModalOpen(false)} />
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      <OcrVerifyDialog
        open={ocrDialogOpen}
        onClose={() => setOcrDialogOpen(false)}
        customerId={customerId}
        dateFrom={date}
        dateTo={date}
        companyOpeningBalance={companyOpeningBalance}
        companyOpeningBalanceDate={companyOpeningBalanceDate}
        systemPayments={systemPaymentsForOcr}
        onConfirmed={() => {
          load();
          if (onVerified) onVerified();
        }}
      />
    </>
  );
}

export default DayExpensesModal;
